import React, { useContext } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Flex from '@wix/da-ds/pkg/Flex';
import DreamUpStars from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { BiData, DreamupInfoClickBiEvent } from '@wix/da-bi/pkg/events';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import useSubmitButtonProps from '../../../../_partials/SubmitButton/useSubmitButtonProps';

import { DailyChallengeArray } from '../types';
import s from './SelectedDailyChallengeHeading.scss';

export interface Props {
  tag: string;
  tagItemId: number;
  dailyChallenges: DailyChallengeArray;
  className?: string;
}

// todo maybe we don't need the full challenge? just tag and isToday?
const SelectedDailyChallengeHeading: React.FC<Props> = ({
  tag,
  tagItemId,
  dailyChallenges,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { aiContentFilter } = useContext(SessionContext);
  const submitButtonProps = useSubmitButtonProps({
    tags: tag,
    biSectionname: 'tags',
    forceEclipseSubmit: isMobile,
  });

  const isAiContentSuppressed = aiContentFilter === 'suppress';

  if (!tag || !Array.isArray(dailyChallenges)) {
    return null;
  }

  // we don't have anything from them to tell -which- challenge matched the current tag
  const { today } =
    dailyChallenges.find(challenge => challenge.tag.tag === tag) || {};

  return (
    <Section className={classnames(className, s['root'])}>
      <Heading className={s['current-tag']}>{`#${tag}`}</Heading>

      <p className={s['challenge-text']}>
        <Trans
          i18nKey="tagPage.dailyChallenge.challengeText"
          specifier={t(
            today
              ? 'tagPage.dailyChallenge.challengeText.themeSpecifier.today'
              : 'tagPage.dailyChallenge.challengeText.themeSpecifier.notToday'
          )}
        >
          Create and submit artwork each day using the
          <a href={t('tagPage.dailyChallenge.challengeUrl')}>Drawtober 2023</a>
          prompt list to earn Profile badges!
        </Trans>
      </p>
      <Flex className={s['submit-container']}>
        {!isAiContentSuppressed && (
          <GhostButton
            variant="main"
            size="large"
            className={s['dreamup-cta']}
            icon={DreamUpStars}
            href={Url.dreamupLink({ prompt: tag })}
            biData={BiData<DreamupInfoClickBiEvent>({
              evid: 923,
              typeid: ResourceType.DISCOVERYTAG,
              itemid: tagItemId,
            })}
          >
            {isMobile
              ? t('tagPage.dailyChallenge.createWithDreamup.mobileText')
              : t('tagPage.dailyChallenge.createWithDreamup.text')}
          </GhostButton>
        )}
        {isMobile ? (
          <SolidButton
            className={s['submit-button']}
            variant="approval-green"
            size="large"
            {...submitButtonProps}
          >
            {t('tagPage.dailyChallenge.submitYourArt')}
          </SolidButton>
        ) : (
          <SolidButton
            className={s['submit-button']}
            variant="approval-green"
            size="large"
            {...submitButtonProps}
          >
            {t('tagPage.dailyChallenge.submitTo', { tag })}
          </SolidButton>
        )}
      </Flex>
    </Section>
  );
};
SelectedDailyChallengeHeading.displayName = 'SelectedDailyChallengeHeading';

export default SelectedDailyChallengeHeading;
